import { useState, useEffect } from "react";
import apiClient from "@/utils/apiClient";
import downloadFile from "@/utils/fileDownloader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Plus,
  FileText,
  Download,
  Upload,
  Eye,
  Clock,
  Filter,
  User,
  Award,
  Trash2,
  TrendingUp,
  Edit2,
  Check,
  X,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Search,
  GraduationCap,
  ArrowUpDown,
  SlidersHorizontal,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/App";
import { examTypesCollection } from "@/db/collection";
import { Badge } from "../ui/badge";
const ResultsManagement = () => {
  const examTypesData = examTypesCollection();

  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [results, setResults] = useState([]);
  const [selectedMarksheet, setSelectedMarksheet] = useState(null);
  const [showMarksheetModal, setShowMarksheetModal] = useState(false);
  const [classes, setClasses] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [showAdditionalModal, setShowAdditionalModal] = useState(false);
  const [additionalFile, setAdditionalFile] = useState(null);
  const [examsList, setExamsList] = useState([]);
  const [selectedExamId, setSelectedExamId] = useState("");
  const [globalStats, setGlobalStats] = useState(null);
  const [examSearch, setExamSearch] = useState("");
  const [showSearchList, setShowSearchList] = useState(false);

  // Single student edit & delete state
  const [editingSubjectId, setEditingSubjectId] = useState(null);
  const [editForm, setEditForm] = useState({ result_id: "", marks_obtained: "", total_marks: "", subject_name: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingMarksheet, setDeletingMarksheet] = useState(false);

  // Bulk delete state
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteForm, setBulkDeleteForm] = useState({
    exam_type: "",
    exam_date: "",
    class_name: "",
    confirm_text: "",
  });
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Overwrite state for bulk upload
  const [overwriteExisting, setOverwriteExisting] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(9); // 9 cards per page (3x3 grid)



  // Filters
  const [filters, setFilters] = useState({
    search: "",
    class_name: "",
    exam_type: "",
    status: "",
    grade: "",
    sort_by: "name_asc",
  });

  const [formData, setFormData] = useState({
    student_id: "",
    exam_type: "",
    exam_date: new Date().toISOString().split("T")[0],
    subjects: [],
  });

  // Exam metadata form for bulk upload
  const [examMetadata, setExamMetadata] = useState({
    examType: "",
    examDate: new Date().toISOString().split("T")[0],
    subjects: [],
    className: "", // Optional class filter
  });

  const [subjectForm, setSubjectForm] = useState({
    name: "",
    maxMarks: "",
  });

  const today = new Date().toISOString().split("T")[0];
  const [currentSubject, setCurrentSubject] = useState({
    subject_name: "",
    marks: "",
    max_marks: "",
  });

  useEffect(() => {
    fetchStudents();
    fetchResults();
    fetchExamTypes();
    fetchSubjects();
    fetchClasses();
    fetchExams();
  }, []);

  const fetchExams = async () => {
    try {
      const response = await apiClient.get("/v1/admin/results-v2/exams");
      setExamsList(response.data.exams || []);
    } catch (error) {

      console.error("Error fetching exams:", error);
    }
  };


  const fetchStudents = async () => {
    try {
      const response = await apiClient.get(
        `/v1/admin/students/?hostel_id=${user.hostel_id}`
      );

      setStudents(response.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await apiClient.get(
        `/v1/admin/classes/?hostel_id=${user.hostel_id}`
      );

      const uniqueClasses = [
        ...new Set(response.data?.map((s) => s.name).filter(Boolean)),
      ];
      setClasses(uniqueClasses.sort());
    } catch (error) {
      console.error(error);
    }
  };

  const fetchResults = async () => {
    try {
      const response = await apiClient.get("/v1/admin/results/recent");
      const list = Array.isArray(response.data) ? response.data : (response.data?.results || []);
      setResults(list);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [
    filters.search,
    filters.class_name,
    filters.exam_type,
    filters.status,
    filters.grade,
    filters.sort_by,
    pageSize,
  ]);

  const fetchExamTypes = async () => {
    try {
      const response = await apiClient.get("/v1/admin/results/exam-types");
      setExamTypes(response.data.exam_types || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await apiClient.get("/v1/admin/results/subjects");
      setSubjects(response.data.subjects || []);
    } catch (error) {
      console.error(error);
    }
  };

  const addSubject = () => {
    const marks = parseInt(currentSubject.marks);
    const maxMarks = parseInt(currentSubject.max_marks);

    // 1. Check if fields are empty
    if (
      !currentSubject.subject_name ||
      isNaN(marks) ||
      isNaN(maxMarks)
    ) {
      toast.error("Please fill all subject fields with valid numbers");
      return;
    }

    // 2. Validate that marks do not exceed max marks
    if (marks > maxMarks) {
      toast.error(`Marks (${marks}) cannot be higher than Max Marks (${maxMarks})`);
      return;
    }

    // 3. Update Form Data
    setFormData({
      ...formData,
      subjects: [
        ...formData.subjects,
        {
          subject_name: currentSubject.subject_name,
          marks: marks,
          max_marks: maxMarks,
        },
      ],
    });

    // 4. Reset temporary input state
    setCurrentSubject({ subject_name: "", marks: "", max_marks: "" });
  };

  const removeSubject = (index) => {
    setFormData({
      ...formData,
      subjects: formData.subjects.filter((_, i) => i !== index),
    });
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (formData.subjects.length === 0) {
      toast.error("Please add at least one subject");
      return;
    }

    const resultsWithCalculation = formData.subjects.map((sub) => {
      const marks = parseFloat(sub.marks) || 0;
      const total = parseFloat(sub.max_marks) || 0;
      const percentage = total > 0 ? ((marks / total) * 100).toFixed(2) : 0;

      return {
        subject: sub.subject_name,
        marks_obtained: marks,
        total_marks: total,
        percentage: parseFloat(percentage),
      };
    });

    // Determine final exam type
    const finalExamType =
      formData?.exam_type === "custom"
        ? formData?.custom_exam_type?.trim()
        : formData?.exam_type;
    // replace final exam upper to lower case and in space use -
    finalExamType?.toLowerCase().replaceAll(/\s+/g, "-");

    setLoading(true);
    try {
      const response = await apiClient.post(
        "/v1/admin/student_records/results/bulk",
        {
          student_id: formData.student_id,
          exam_type: finalExamType,
          exam_date: formData.exam_date,
          results: resultsWithCalculation,
        }
      );
      if (response?.data?.message) {
        toast.success(response.data.message);
      } else {
        toast.success(
          `✅ Results added for ${resultsWithCalculation.length} subject(s)!`
        );
      }
      setShowAddDialog(false);
      setFormData({
        student_id: "",
        exam_type: "",
        exam_date: new Date().toISOString().split("T")[0],
        subjects: [],
      });
      fetchResults();
      fetchExamTypes();
      fetchSubjects();
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          error.response?.data?.error ||
          "Failed to add results"
      );
    } finally {
      setLoading(false);
    }
  };

  // Add subject to exam metadata
  const handleAddSubject = () => {
    if (!subjectForm.name.trim()) {
      toast.error("Please enter subject name");
      return;
    }
    if (!subjectForm.maxMarks || parseFloat(subjectForm.maxMarks) <= 0) {
      toast.error("Please enter valid max marks");
      return;
    }
    if (examMetadata.subjects.length >= 15) {
      toast.error("Maximum 15 subjects allowed");
      return;
    }

    setExamMetadata((prev) => ({
      ...prev,
      subjects: [
        ...prev.subjects,
        {
          id: Date.now(),
          name: subjectForm.name.trim(),
          maxMarks: parseFloat(subjectForm.maxMarks),
        },
      ],
    }));
    setSubjectForm({ name: "", maxMarks: "" });
  };

  const handleRemoveSubject = (subjectId) => {
    setExamMetadata((prev) => ({
      ...prev,
      subjects: prev.subjects.filter((s) => s.id !== subjectId),
    }));
  };

  const handleBulkDownload = async () => {
    // Validate exam metadata
    if (!examMetadata.examType.trim()) {
      toast.error("Please enter exam name/type");
      return;
    }
    if (
      examMetadata.examType === "custom" &&
      !examMetadata.custom_exam_type.trim()
    ) {
      toast.error("Please enter custom exam type");
      return;
    }

    if (!examMetadata.examDate) {
      toast.error("Please select exam date");
      return;
    }
    if (examMetadata.subjects.length === 0) {
      toast.error("Please add at least one subject");
      return;
    }

    try {
      // Build subjects param: "Math:100,Science:100"
      const subjectsParam = examMetadata.subjects
        .map((s) => `${s.name}:${s.maxMarks}`)
        .join(",");

      const finalExamType =
        examMetadata?.examType === "custom"
          ? examMetadata?.custom_exam_type?.trim()
          : examMetadata?.examType;
      // replace final exam upper to lower case and in space use -
      finalExamType?.toLowerCase().replaceAll(/\s+/g, "-");

      const params = {
        exam_type: finalExamType,
        exam_date: examMetadata.examDate,
        subjects: subjectsParam,
      };

      // Add class filter if selected
      if (examMetadata.className) {
        params.class_name = examMetadata.className;
      }

      const response = await apiClient.get("/v1/admin/results/template", {
        params,
        responseType: "blob",
      });

      const safeExamType = examMetadata.examType.replace(/[^a-zA-Z0-9]/g, "_");
      const classStr = examMetadata.className
        ? `_${examMetadata.className}`
        : "";
      const fileName = `results_${safeExamType}${classStr}_${examMetadata.examDate}.xlsx`;
      const mimeType = response.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      await downloadFile(response.data, fileName, mimeType);
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          "Failed to download template for no student found"
      );
    }
  };

  const handleBasicTemplateDownload = async () => {
    try {
      const params = {};
      if (examMetadata.className) {
        params.class_name = examMetadata.className;
      }

      const response = await apiClient.get("/v1/admin/results/template-basic", {
        params,
        responseType: "blob",
      });

      const mimeType = response.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      await downloadFile(response.data, "results_template_admin.xlsx", mimeType);
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Failed to download template"
      );
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) {
      toast.error("Please select a file");
      return;
    }

    const formDataObj = new FormData();
    formDataObj.append("file", bulkFile);

    setLoading(true);
    try {
      const uploadUrl = overwriteExisting
        ? "/v1/admin/results/bulk-upload?overwrite=true"
        : "/v1/admin/results/bulk-upload";

      const response = await apiClient.post(
        uploadUrl,
        formDataObj,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data.successful > 0) {
        toast.success(
          `Results uploaded: ${response.data.successful} successful, ${response.data.failed} failed`
        );
      }
      
      // Show all errors (including marks exceeding maximum)
      if (response.data.errors && response.data.errors.length > 0) {
        const errorsToShow = response.data.errors.slice(0, 5);
        errorsToShow.forEach((err, idx) => {
          setTimeout(() => {
            toast.error(err, { duration: 10000 });
          }, (idx + 1) * 300);
        });
        
        if (response.data.errors.length > 5) {
          setTimeout(() => {
            toast.error(
              `... and ${response.data.errors.length - 5} more error(s). Please fix your Excel file.`,
              { duration: 10000 }
            );
          }, 2000);
        }
      }
      
      if (response.data.successful === 0 && response.data.failed > 0) {
        toast.error(`Upload failed: All ${response.data.failed} records had errors`);
      }
      
      setBulkFile(null);
      fetchResults();
      fetchExamTypes();
      fetchSubjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload results");
    } finally {
      setLoading(false);
    }
  };


  const handleAdditionalTemplateDownload = async () => {
    const examId = selectedExamId || "sample";
    try {
      const response = await apiClient.get(`/v1/admin/results-v2/additional-template/${examId}`, {
        responseType: "blob",
      });
      const mimeType = response.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      await downloadFile(response.data, `day_scholar_template_${examId}.xlsx`, mimeType);
    } catch (error) {
      toast.error("Failed to download additional template");
    }
  };

  const handleAdditionalUpload = async () => {
    if (!selectedExamId || !additionalFile) {
      toast.error("Please select an exam and a file");
      return;
    }

    const formDataObj = new FormData();
    formDataObj.append("file", additionalFile);

    setLoading(true);
    try {
      const response = await apiClient.post(
        `/v1/admin/results-v2/upload/additional/${selectedExamId}`,
        formDataObj,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      
      setGlobalStats(response.data.global_stats);
      toast.success(response.data.message);
      setAdditionalFile(null);
      fetchResults();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload additional results");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSubjectEdit = async (resultId) => {
    const finalResultId = resultId || editForm.result_id;
    if (!finalResultId) {
      toast.error("Unable to identify the record ID to save");
      return;
    }

    const marks = parseFloat(editForm.marks_obtained);
    const maxMarks = parseFloat(editForm.total_marks);

    if (isNaN(marks) || isNaN(maxMarks)) {
      toast.error("Please enter valid numeric marks");
      return;
    }
    if (marks < 0) {
      toast.error("Marks cannot be negative");
      return;
    }
    if (marks > maxMarks) {
      toast.error(`Marks (${marks}) cannot exceed Max Marks (${maxMarks})`);
      return;
    }

    setSavingEdit(true);
    try {
      await apiClient.put(`/v1/admin/results/${finalResultId}`, {
        marks_obtained: marks,
        total_marks: maxMarks,
      });
      toast.success("Marks updated successfully");

      if (selectedMarksheet) {
        const updatedSubjects = selectedMarksheet.subjects.map((s, sIdx) => {
          const sRowKey = s.id || `${selectedMarksheet.student_id || 'std'}-${s.subject_name || 'sub'}-${sIdx}`;
          if (s.id === finalResultId || sRowKey === editingSubjectId) {
            const newPct = maxMarks > 0 ? parseFloat(((marks / maxMarks) * 100).toFixed(2)) : 0;
            return {
              ...s,
              marks_obtained: marks,
              total_marks: maxMarks,
              percentage: newPct,
            };
          }
          return s;
        });

        const newTotalObtained = updatedSubjects.reduce((sum, s) => sum + parseFloat(s.marks_obtained || 0), 0);
        const newTotalMax = updatedSubjects.reduce((sum, s) => sum + parseFloat(s.total_marks || 0), 0);
        const newOverallPct = newTotalMax > 0 ? parseFloat(((newTotalObtained / newTotalMax) * 100).toFixed(2)) : 0;

        const calcGrade = (pct) => {
          if (pct >= 90) return "A+";
          if (pct >= 80) return "A";
          if (pct >= 70) return "B";
          if (pct >= 60) return "C";
          if (pct >= 50) return "D";
          if (pct >= 40) return "E";
          return "F";
        };

        setSelectedMarksheet({
          ...selectedMarksheet,
          subjects: updatedSubjects,
          total_obtained: newTotalObtained,
          total_max: newTotalMax,
          overall_percentage: newOverallPct,
          grade: calcGrade(newOverallPct),
          status: newOverallPct >= 33 ? "PASSED" : "FAILED",
        });
      }

      setEditingSubjectId(null);
      fetchResults();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update marks");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteMarksheet = async (studentId, examType, examDate, studentName) => {
    const formattedDate = examDate ? new Date(examDate).toLocaleDateString() : "";
    if (
      !confirm(
        `Are you sure you want to delete the complete marksheet for "${studentName || 'this student'}" (${examType}${formattedDate ? ` - ${formattedDate}` : ''})? This will remove all subject marks and cannot be undone.`
      )
    ) {
      return;
    }

    setDeletingMarksheet(true);
    try {
      const response = await apiClient.post("/v1/admin/results/delete-marksheet", {
        student_id: studentId,
        exam_type: examType,
        exam_date: examDate,
      });
      toast.success(response.data.message || "Marksheet deleted successfully");
      setShowMarksheetModal(false);
      setSelectedMarksheet(null);
      fetchResults();
      fetchExamTypes();
      fetchSubjects();
      fetchExams();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete marksheet");
    } finally {
      setDeletingMarksheet(false);
    }
  };

  const handleBulkDeleteExam = async () => {
    if (!bulkDeleteForm.exam_type) {
      toast.error("Please select an exam type to delete");
      return;
    }
    if (bulkDeleteForm.confirm_text !== "DELETE") {
      toast.error("Please type DELETE to confirm");
      return;
    }

    setBulkDeleting(true);
    try {
      const payload = {
        exam_type: bulkDeleteForm.exam_type,
      };
      if (bulkDeleteForm.exam_date) {
        payload.exam_date = bulkDeleteForm.exam_date;
      }
      if (bulkDeleteForm.class_name && bulkDeleteForm.class_name !== "all") {
        payload.class_name = bulkDeleteForm.class_name;
      }

      const response = await apiClient.post("/v1/admin/results/bulk-delete", payload);
      toast.success(response.data.message || `Deleted ${response.data.deleted_count} records`);
      setShowBulkDeleteModal(false);
      setBulkDeleteForm({ exam_type: "", exam_date: "", class_name: "", confirm_text: "" });
      fetchResults();
      fetchExamTypes();
      fetchSubjects();
      fetchExams();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to bulk delete results");
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleDeleteResult = async (resultId) => {
    if (!confirm("Are you sure you want to delete this subject result?")) return;

    try {
      await apiClient.delete(`/v1/admin/results/${resultId}`);
      toast.success("Subject result deleted successfully");

      if (selectedMarksheet) {
        const remaining = selectedMarksheet.subjects.filter((s) => s.id !== resultId);
        if (remaining.length === 0) {
          setShowMarksheetModal(false);
          setSelectedMarksheet(null);
        } else {
          const newTotalObtained = remaining.reduce((sum, s) => sum + parseFloat(s.marks_obtained || 0), 0);
          const newTotalMax = remaining.reduce((sum, s) => sum + parseFloat(s.total_marks || 0), 0);
          const newOverallPct = newTotalMax > 0 ? parseFloat(((newTotalObtained / newTotalMax) * 100).toFixed(2)) : 0;
          const calcGrade = (pct) => {
            if (pct >= 90) return "A+";
            if (pct >= 80) return "A";
            if (pct >= 70) return "B";
            if (pct >= 60) return "C";
            if (pct >= 50) return "D";
            if (pct >= 40) return "E";
            return "F";
          };
          setSelectedMarksheet({
            ...selectedMarksheet,
            subjects: remaining,
            total_obtained: newTotalObtained,
            total_max: newTotalMax,
            overall_percentage: newOverallPct,
            grade: calcGrade(newOverallPct),
            status: newOverallPct >= 33 ? "PASSED" : "FAILED",
          });
        }
      }
      fetchResults();
      fetchSubjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete result");
    }
  };

  // Dynamic lists for category filters
  const availableClasses = [
    ...new Set([
      ...classes,
      ...results.map((r) => r.class_name).filter(Boolean),
    ]),
  ].sort((a, b) => a.localeCompare(undefined, { numeric: true, sensitivity: "base" }));

  const availableExamTypes = [
    ...new Set([
      ...examTypes,
      ...results.flatMap((r) => r.marksheets?.map((m) => m.exam_type) || []).filter(Boolean),
    ]),
  ].sort();

  const classCounts = results.reduce((acc, curr) => {
    const cls = curr.class_name || "Unknown";
    acc[cls] = (acc[cls] || 0) + 1;
    return acc;
  }, {});

  // Filter and sort results
  const filteredResults = results
    .filter((group) => {
      // 1. Search Query: Student Name or Readable ID
      if (filters.search) {
        const query = filters.search.toLowerCase().trim();
        const matchesName = group.student_name?.toLowerCase().includes(query);
        const matchesId = group.student_readable_id?.toLowerCase().includes(query);
        if (!matchesName && !matchesId) return false;
      }

      // 2. Class category filter
      if (filters.class_name) {
        if (group.class_name?.toLowerCase() !== filters.class_name.toLowerCase()) {
          return false;
        }
      }

      // 3. Exam Type category filter
      if (filters.exam_type) {
        const hasMatchingExamType = group.marksheets?.some(
          (exam) => exam.exam_type?.toLowerCase() === filters.exam_type.toLowerCase()
        );
        if (!hasMatchingExamType) return false;
      }

      // 4. Pass / Fail Status filter
      if (filters.status) {
        const relevantMarksheets = filters.exam_type
          ? group.marksheets?.filter(
              (m) => m.exam_type?.toLowerCase() === filters.exam_type.toLowerCase()
            )
          : group.marksheets;
        const matchesStatus = relevantMarksheets?.some(
          (m) => m.status?.toUpperCase() === filters.status.toUpperCase()
        );
        if (!matchesStatus) return false;
      }

      // 5. Grade category filter
      if (filters.grade) {
        const relevantMarksheets = filters.exam_type
          ? group.marksheets?.filter(
              (m) => m.exam_type?.toLowerCase() === filters.exam_type.toLowerCase()
            )
          : group.marksheets;
        const matchesGrade = relevantMarksheets?.some(
          (m) => m.grade?.toUpperCase() === filters.grade.toUpperCase()
        );
        if (!matchesGrade) return false;
      }

      return true;
    })
    .sort((a, b) => {
      if (filters.sort_by === "name_asc") {
        return (a.student_name || "").localeCompare(b.student_name || "");
      }
      if (filters.sort_by === "name_desc") {
        return (b.student_name || "").localeCompare(a.student_name || "");
      }
      if (filters.sort_by === "class_asc") {
        return (a.class_name || "").localeCompare(b.class_name || "", undefined, { numeric: true });
      }
      if (filters.sort_by === "pct_desc") {
        const getPct = (st) => {
          const list = filters.exam_type
            ? st.marksheets?.filter((m) => m.exam_type?.toLowerCase() === filters.exam_type.toLowerCase())
            : st.marksheets;
          return Math.max(...(list?.map((m) => m.overall_percentage) || [0]), 0);
        };
        return getPct(b) - getPct(a);
      }
      if (filters.sort_by === "pct_asc") {
        const getPct = (st) => {
          const list = filters.exam_type
            ? st.marksheets?.filter((m) => m.exam_type?.toLowerCase() === filters.exam_type.toLowerCase())
            : st.marksheets;
          return Math.min(...(list?.map((m) => m.overall_percentage) || [100]), 100);
        };
        return getPct(a) - getPct(b);
      }
      return 0;
    });

  const activeFiltersCount = [
    filters.search,
    filters.class_name,
    filters.exam_type,
    filters.status,
    filters.grade,
    filters.sort_by !== "name_asc" ? filters.sort_by : "",
  ].filter(Boolean).length;

  const handleClearFilters = () => {
    setFilters({
      search: "",
      class_name: "",
      exam_type: "",
      status: "",
      grade: "",
      sort_by: "name_asc",
    });
  };

  // Pagination calculation
  const totalStudents = filteredResults.length;
  const effectivePageSize = pageSize === "all" ? (totalStudents || 1) : pageSize;
  const totalPages = Math.max(1, Math.ceil(totalStudents / effectivePageSize));
  const validCurrentPage = Math.min(Math.max(1, currentPage), totalPages);
  const startIndex = (validCurrentPage - 1) * effectivePageSize;
  const endIndex = Math.min(startIndex + effectivePageSize, totalStudents);
  const paginatedResults = pageSize === "all"
    ? filteredResults
    : filteredResults.slice(startIndex, endIndex);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold" style={{ color: "#2C5F7F" }}>
          Results Management
        </h1>
        <Button
          onClick={() => setShowAddDialog(true)}
          className="btn-primary text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Results
        </Button>
      </div>

      <Tabs defaultValue="view">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="view">View Results</TabsTrigger>
          <TabsTrigger value="add">Add Results</TabsTrigger>
          <TabsTrigger value="bulk">Bulk Upload</TabsTrigger>
          <TabsTrigger value="additional">Day Scholar Sync</TabsTrigger>
        </TabsList>


        <TabsContent value="view">
          {/* Filters Card */}
          <Card className="mb-5 shadow-sm border border-gray-200">
            <CardHeader className="flex flex-row items-center justify-between pb-3 border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 bg-blue-50 text-blue-600 rounded-lg">
                  <Filter className="w-4 h-4" />
                </div>
                <div>
                  <CardTitle className="text-base font-bold text-gray-800 flex items-center gap-2">
                    Filter & Category View
                    {activeFiltersCount > 0 && (
                      <span className="px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700 rounded-full">
                        {activeFiltersCount} active
                      </span>
                    )}
                  </CardTitle>
                  <p className="text-xs text-gray-500">
                    Filter results class-wise, exam-wise, or by performance category
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {activeFiltersCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearFilters}
                    className="text-xs text-gray-600 hover:text-gray-900 gap-1 h-8"
                  >
                    <X className="w-3.5 h-3.5" />
                    Reset
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBulkDeleteModal(true)}
                  className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 gap-1.5 h-8 text-xs font-medium"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Bulk Delete
                </Button>
              </div>
            </CardHeader>

            <CardContent className="pt-4 space-y-4">
              {/* Category Pills: Class-wise quick selector */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-600 flex items-center gap-1.5 uppercase tracking-wider">
                    <GraduationCap className="w-3.5 h-3.5 text-blue-600" />
                    Class Category
                  </span>
                  {filters.class_name && (
                    <button
                      type="button"
                      onClick={() => setFilters((prev) => ({ ...prev, class_name: "" }))}
                      className="text-[11px] text-blue-600 hover:underline font-medium"
                    >
                      Show all classes
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar">
                  <button
                    type="button"
                    onClick={() => setFilters((prev) => ({ ...prev, class_name: "" }))}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all whitespace-nowrap flex items-center gap-1.5 ${
                      !filters.class_name
                        ? "bg-blue-600 text-white shadow-sm font-semibold"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    All Classes
                    <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${!filters.class_name ? "bg-blue-700 text-white" : "bg-gray-200 text-gray-600"}`}>
                      {results.length}
                    </span>
                  </button>

                  {availableClasses.map((cls) => {
                    const count = classCounts[cls] || 0;
                    const isSelected = filters.class_name.toLowerCase() === cls.toLowerCase();
                    return (
                      <button
                        key={cls}
                        type="button"
                        onClick={() =>
                          setFilters((prev) => ({
                            ...prev,
                            class_name: isSelected ? "" : cls,
                          }))
                        }
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all whitespace-nowrap flex items-center gap-1.5 ${
                          isSelected
                            ? "bg-blue-600 text-white shadow-sm font-semibold"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        {cls}
                        <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${isSelected ? "bg-blue-700 text-white" : "bg-gray-200 text-gray-600"}`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Detailed Filter Controls */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 pt-1">
                {/* 1. Search */}
                <div className="lg:col-span-2">
                  <Label className="text-xs font-medium text-gray-600 mb-1 block">
                    Search Student
                  </Label>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400 pointer-events-none" />
                    <Input
                      placeholder="Search by name or ID..."
                      className="pl-9 h-9 text-xs bg-white border-gray-200"
                      value={filters.search}
                      onChange={(e) =>
                        setFilters({ ...filters, search: e.target.value })
                      }
                    />
                    {filters.search && (
                      <button
                        type="button"
                        onClick={() => setFilters({ ...filters, search: "" })}
                        className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* 2. Class Dropdown */}
                <div>
                  <Label className="text-xs font-medium text-gray-600 mb-1 block">
                    Class
                  </Label>
                  <select
                    className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md h-9 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    value={filters.class_name}
                    onChange={(e) =>
                      setFilters({ ...filters, class_name: e.target.value })
                    }
                  >
                    <option value="">All Classes</option>
                    {availableClasses.map((cls) => (
                      <option key={cls} value={cls}>
                        {cls}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 3. Exam Type */}
                <div>
                  <Label className="text-xs font-medium text-gray-600 mb-1 block">
                    Exam Type
                  </Label>
                  <select
                    className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md h-9 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    value={filters.exam_type}
                    onChange={(e) =>
                      setFilters({ ...filters, exam_type: e.target.value })
                    }
                  >
                    <option value="">All Exams</option>
                    {availableExamTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 4. Pass / Fail Status */}
                <div>
                  <Label className="text-xs font-medium text-gray-600 mb-1 block">
                    Status
                  </Label>
                  <select
                    className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md h-9 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    value={filters.status}
                    onChange={(e) =>
                      setFilters({ ...filters, status: e.target.value })
                    }
                  >
                    <option value="">All Status</option>
                    <option value="PASSED">Passed Only</option>
                    <option value="FAILED">Failed Only</option>
                  </select>
                </div>

                {/* 5. Grade Controls */}
                <div>
                  <Label className="text-xs font-medium text-gray-600 mb-1 block">
                    Grade
                  </Label>
                  <select
                    className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md h-9 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    value={filters.grade}
                    onChange={(e) =>
                      setFilters({ ...filters, grade: e.target.value })
                    }
                  >
                    <option value="">All Grades</option>
                    <option value="A+">Grade A+ (≥90%)</option>
                    <option value="A">Grade A (≥80%)</option>
                    <option value="B">Grade B (≥70%)</option>
                    <option value="C">Grade C (≥60%)</option>
                    <option value="D">Grade D (≥50%)</option>
                    <option value="E">Grade E (≥40%)</option>
                    <option value="F">Grade F (&lt;40%)</option>
                  </select>
                </div>
              </div>

              {/* Active Filters Summary & Sort Row */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 pt-2 border-t border-gray-100 text-xs">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-gray-500 font-medium">
                    Showing <strong className="text-gray-900">{filteredResults.length}</strong> of <strong className="text-gray-900">{results.length}</strong> students
                  </span>

                  {/* Active tags */}
                  {filters.class_name && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-[11px]">
                      Class: {filters.class_name}
                      <button
                        type="button"
                        onClick={() => setFilters((prev) => ({ ...prev, class_name: "" }))}
                        className="hover:text-blue-900"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {filters.exam_type && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 text-[11px]">
                      Exam: {filters.exam_type}
                      <button
                        type="button"
                        onClick={() => setFilters((prev) => ({ ...prev, exam_type: "" }))}
                        className="hover:text-indigo-900"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {filters.status && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${
                      filters.status === "PASSED"
                        ? "bg-green-50 text-green-700 border-green-200"
                        : "bg-red-50 text-red-700 border-red-200"
                    }`}>
                      Status: {filters.status}
                      <button
                        type="button"
                        onClick={() => setFilters((prev) => ({ ...prev, status: "" }))}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {filters.grade && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[11px]">
                      Grade: {filters.grade}
                      <button
                        type="button"
                        onClick={() => setFilters((prev) => ({ ...prev, grade: "" }))}
                        className="hover:text-amber-900"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {filters.search && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-300 text-[11px]">
                      Search: &ldquo;{filters.search}&rdquo;
                      <button
                        type="button"
                        onClick={() => setFilters((prev) => ({ ...prev, search: "" }))}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {activeFiltersCount > 0 && (
                    <button
                      type="button"
                      onClick={handleClearFilters}
                      className="text-blue-600 hover:underline font-semibold ml-1 text-[11px]"
                    >
                      Clear All
                    </button>
                  )}
                </div>

                {/* Sort selector */}
                <div className="flex items-center gap-1.5 text-gray-500 self-end sm:self-auto">
                  <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-[11px]">Sort:</span>
                  <select
                    className="px-2 py-1 text-xs border border-gray-200 rounded bg-white text-gray-700 focus:outline-none"
                    value={filters.sort_by}
                    onChange={(e) => setFilters({ ...filters, sort_by: e.target.value })}
                  >
                    <option value="name_asc">Name (A → Z)</option>
                    <option value="name_desc">Name (Z → A)</option>
                    <option value="class_asc">Class (Ascending)</option>
                    <option value="pct_desc">Highest Percentage</option>
                    <option value="pct_asc">Lowest Percentage</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredResults.length === 0 ? (
              <Card className="col-span-full border-dashed">
                <CardContent className="p-10 text-center space-y-3">
                  <div className="w-12 h-12 mx-auto bg-gray-100 rounded-full flex items-center justify-center text-gray-400">
                    <Filter className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700">No results match your criteria</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Try clearing or adjusting your class, exam, or search filters.
                    </p>
                  </div>
                  {activeFiltersCount > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleClearFilters}
                      className="mt-2 text-xs"
                    >
                      Clear Filters
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
                paginatedResults?.map((group, index) => {
                  const displayedMarksheets = filters.exam_type
                    ? group.marksheets?.filter(
                        (m) => m.exam_type?.toLowerCase() === filters.exam_type.toLowerCase()
                      )
                    : group.marksheets;
                  const finalMarksheets = (displayedMarksheets && displayedMarksheets.length > 0)
                    ? displayedMarksheets
                    : group.marksheets;

                  return (
                    <Card
                      key={`${group.student_id}-${group.subject}-${index}`}
                      className="hover:shadow-lg transition-shadow border-l-4 border-l-blue-500"
                    >
                      <CardHeader className="pb-2">
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-base flex items-center gap-2">
                              <User className="w-4 h-4 text-blue-500" />
                              {group.student_name}
                            </CardTitle>
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                              ID: {group.student_readable_id} | Class:{" "}
                              <span className="font-semibold text-blue-700">{group.class_name}</span>
                            </p>
                          </div>
                        </div>
                      </CardHeader>

                      <CardContent>
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-gray-400 uppercase">
                            {filters.exam_type ? `${filters.exam_type} Marksheet` : "Available Marksheets"}
                          </p>
                          {finalMarksheets?.map((marksheet, mIdx) => (
                            <div
                              key={`${group.student_id}-${marksheet.exam_type}-${mIdx}`}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-blue-50 hover:border-blue-200 cursor-pointer transition-all group"
                              onClick={() => {
                                setSelectedMarksheet({
                                  ...marksheet,
                                  student_id: group.student_id,
                                  student_name: group.student_name,
                                  student_readable_id: group.student_readable_id,
                                  class_name: group.class_name
                                });
                                setShowMarksheetModal(true);
                              }}
                            >
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-white rounded shadow-sm group-hover:bg-blue-100">
                                  <FileText className="w-4 h-4 text-blue-500" />
                                </div>
                                <div>
                                  <p className="text-sm font-bold text-gray-700">{marksheet.exam_type}</p>
                                  <p className="text-xs text-gray-500">{new Date(marksheet.exam_date).toLocaleDateString()}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="text-right">
                                  <p className="text-sm font-bold text-blue-600">{marksheet.overall_percentage}%</p>
                                  <p className="text-[10px] font-bold text-gray-400 uppercase">{marksheet.grade} | {marksheet.status}</p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                  title="Delete this marksheet"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteMarksheet(group.student_id, marksheet.exam_type, marksheet.exam_date, group.student_name);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
            )}
          </div>

          {/* Pagination Controls */}
          {totalStudents > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <span>
                  Showing <strong className="text-gray-900">{startIndex + 1}</strong> to{" "}
                  <strong className="text-gray-900">{endIndex}</strong> of{" "}
                  <strong className="text-gray-900">{totalStudents}</strong> students
                </span>
                <span className="text-gray-300">|</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-500">Per page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      const val = e.target.value === "all" ? "all" : parseInt(e.target.value);
                      setPageSize(val);
                    }}
                    className="border border-gray-200 rounded px-2 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value={6}>6</option>
                    <option value={9}>9</option>
                    <option value={12}>12</option>
                    <option value={24}>24</option>
                    <option value="all">All</option>
                  </select>
                </div>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={validCurrentPage === 1}
                    onClick={() => setCurrentPage(1)}
                    title="First Page"
                  >
                    <ChevronsLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={validCurrentPage === 1}
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    title="Previous Page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>

                  {/* Page numbers */}
                  <div className="flex items-center gap-1 px-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter((p) => {
                        return (
                          p === 1 ||
                          p === totalPages ||
                          Math.abs(p - validCurrentPage) <= 1
                        );
                      })
                      .reduce((acc, p, idx, arr) => {
                        if (idx > 0 && p - arr[idx - 1] > 1) {
                          acc.push({ type: "ellipsis", key: `ellipsis-${p}` });
                        }
                        acc.push({ type: "page", page: p });
                        return acc;
                      }, [])
                      .map((item) =>
                        item.type === "ellipsis" ? (
                          <span key={item.key} className="px-1 text-xs text-gray-400">
                            ...
                          </span>
                        ) : (
                          <Button
                            key={item.page}
                            variant={item.page === validCurrentPage ? "default" : "outline"}
                            size="sm"
                            className={`h-8 w-8 p-0 text-xs ${
                              item.page === validCurrentPage
                                ? "bg-blue-600 text-white hover:bg-blue-700"
                                : ""
                            }`}
                            onClick={() => setCurrentPage(item.page)}
                          >
                            {item.page}
                          </Button>
                        )
                      )}
                  </div>

                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={validCurrentPage === totalPages}
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    title="Next Page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={validCurrentPage === totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                    title="Last Page"
                  >
                    <ChevronsRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="add">
          <Card>
            <CardHeader>
              <CardTitle>Add Individual Result</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                Click "Add Results" button above to add results for a student,
                or use the Bulk Upload tab for multiple students.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bulk">
          <Card>
            <CardHeader>
              <CardTitle>Bulk Upload Results</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <h3 className="font-semibold mb-2">📋 Instructions:</h3>
                <ol className="text-sm space-y-1 ml-4 list-decimal">
                  <li>Enter exam name/type and select exam date</li>
                  <li>Add subjects with their maximum marks</li>
                  <li>Optionally filter by class</li>
                  <li>
                    Download template (pre-filled with students and subjects)
                  </li>
                  <li>Fill the marks in the template</li>
                  <li>Upload the completed file</li>
                </ol>
              </div>

              {/* Exam Metadata Form */}
              <div className="border rounded-lg p-4 space-y-4 bg-gray-50">
                <h4 className="font-medium text-gray-700">
                  Step 1: Exam Details
                </h4>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="examType">Exam Name/Type *</Label>
                    <select
                      className="w-full px-3 py-2 border rounded-lg mt-1"
                      id="examType"
                      value={examMetadata.examType}
                      onChange={(e) =>
                        setExamMetadata((prev) => ({
                          ...prev,
                          examType: e.target.value,
                        }))
                      }
                    >
                      <option value="">Select Exam Type</option>
                      {examTypesData?.map((item) => (
                        <option key={item?.value} value={item?.value}>
                          {item?.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="examDate">Exam Date *</Label>
                    <Input
                      id="examDate"
                      type="date"
                      max={today}
                      value={examMetadata.examDate}
                      onChange={(e) =>
                        setExamMetadata((prev) => ({
                          ...prev,
                          examDate: e.target.value,
                        }))
                      }
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="className">Class (Optional)</Label>
                    <select
                      id="className"
                      className="w-full px-3 py-2 border rounded-lg mt-1"
                      value={examMetadata.className}
                      onChange={(e) =>
                        setExamMetadata((prev) => ({
                          ...prev,
                          className: e.target.value,
                        }))
                      }
                    >
                      <option value="">All Classes</option>
                      {classes?.map((cls) => (
                        <option key={cls} value={cls}>
                          {cls}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              {/* Custom exam type */}

              {examMetadata.examType === "custom" && (
                <div className="border rounded-lg p-4 space-y-4 bg-gray-50">
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label htmlFor="examName">Exam Name *</Label>
                      <Input
                        id="examName"
                        placeholder="Enter custom exam name"
                        value={examMetadata.custom_exam_type}
                        onChange={(e) =>
                          setExamMetadata((prev) => ({
                            ...prev,
                            custom_exam_type: e.target.value,
                          }))
                        }
                        required
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Subjects Form */}
              <div className="border rounded-lg p-4 space-y-4 bg-gray-50">
                <h4 className="font-medium text-gray-700">
                  Step 2: Add Subjects
                </h4>

                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label htmlFor="subjectName">Subject Name</Label>
                    <Input
                      id="subjectName"
                      placeholder="e.g., Mathematics"
                      value={subjectForm.name}
                      onChange={(e) =>
                        setSubjectForm((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      onKeyPress={(e) =>
                        e.key === "Enter" &&
                        (e.preventDefault(), handleAddSubject())
                      }
                      className="mt-1"
                    />
                  </div>
                  <div className="w-32">
                    <Label htmlFor="maxMarks">Max Marks</Label>
                    <Input
                      id="maxMarks"
                      type="number"
                      placeholder="100"
                      value={subjectForm.maxMarks}
                      onChange={(e) =>
                        setSubjectForm((prev) => ({
                          ...prev,
                          maxMarks: e.target.value,
                        }))
                      }
                      onKeyPress={(e) =>
                        e.key === "Enter" &&
                        (e.preventDefault(), handleAddSubject())
                      }
                      className="mt-1"
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={handleAddSubject}
                    className="btn-primary text-white"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {/* Added Subjects List */}
                {examMetadata.subjects.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      Added Subjects ({examMetadata.subjects.length}):
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {examMetadata.subjects.map((subj) => (
                        <div
                          key={subj.id}
                          className="flex items-center gap-2 bg-white border rounded-full px-3 py-1 text-sm"
                        >
                          <span>{subj.name}</span>
                          <span className="text-gray-400">|</span>
                          <span className="text-blue-600 font-medium">
                            {subj.maxMarks}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveSubject(subj.id)}
                            className="text-red-500 hover:text-red-700 ml-1"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Download Template */}
              <div className="border rounded-lg p-4 space-y-4 bg-gray-50">
                <h4 className="font-medium text-gray-700">
                  Step 3: Download Template
                </h4>

                <div className="grid grid-cols-1 gap-3">
                  <Button
                    onClick={handleBulkDownload}
                    variant="outline"
                    className="w-full"
                    disabled={
                      !examMetadata.examType ||
                      !examMetadata.examDate ||
                      examMetadata.subjects.length === 0
                    }
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Custom Template
                    {examMetadata.examType &&
                      examMetadata.subjects.length > 0 && (
                        <span className="ml-2 text-xs text-gray-500">
                          ({examMetadata.subjects.length} subjects)
                        </span>
                      )}
                  </Button>
                </div>
              </div>

              {/* Upload Section */}
              <div className="border rounded-lg p-4 space-y-4 bg-gray-50">
                <h4 className="font-medium text-gray-700">
                  Step 4: Upload Completed File
                </h4>

                {/* Overwrite mode checkbox */}
                <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <input
                    type="checkbox"
                    id="overwrite-results"
                    checked={overwriteExisting}
                    onChange={(e) => setOverwriteExisting(e.target.checked)}
                    className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                  />
                  <label htmlFor="overwrite-results" className="text-sm cursor-pointer select-none">
                    <span className="font-semibold text-amber-900 block">
                      Update / Overwrite Existing Results
                    </span>
                    <span className="text-xs text-amber-700 block">
                      If checked, existing student marks for matching exam and subject will be updated with the uploaded values instead of skipping/failing.
                    </span>
                  </label>
                </div>

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-white">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setBulkFile(e.target.files[0])}
                    className="hidden"
                    id="results-bulk-file"
                  />
                  <label htmlFor="results-bulk-file" className="cursor-pointer">
                    <Upload className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">
                      {bulkFile
                        ? bulkFile.name
                        : "Click to select completed file"}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Supports .xlsx and .xls files
                    </p>
                  </label>
                </div>

                {bulkFile && (
                  <Button
                    onClick={handleBulkUpload}
                    disabled={loading}
                    className="w-full btn-primary text-white"
                  >
                    {loading ? "Uploading..." : overwriteExisting ? "Upload & Overwrite Results" : "Upload Results"}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="additional">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                Additional Upload (Day Scholar Data)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                <h3 className="font-semibold text-green-800 mb-2">🎯 Why use this?</h3>
                <p className="text-sm text-green-700">
                  To show accurate <strong>Class Averages</strong> and <strong>Class Highest</strong> on parent marksheets, 
                  you must include Day Scholar marks. This tool merges external marks with your existing hosteller data.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Left: Instructions & Resources */}
                <div className="space-y-4">
                  <div className="border p-4 rounded-lg bg-gray-50">
                    <h4 className="font-medium flex items-center gap-2 mb-3">
                      <Download className="w-4 h-4 text-blue-600" />
                      Resources & Templates
                    </h4>
                    <p className="text-xs text-gray-500 mb-3">
                      Download the pre-filled sheet to see the required format. 
                      Select an exam to get a custom sheet with your institutional subjects.
                    </p>
                    <Button 
                      variant="outline" 
                      className="w-full text-blue-600 border-blue-200 hover:bg-blue-50 bg-white"
                      onClick={handleAdditionalTemplateDownload}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Download Sample Day Scholar Sheet
                    </Button>
                  </div>

                  <div className="border p-4 rounded-lg bg-gray-50">
                    <h4 className="font-medium flex items-center gap-2">
                      <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">1</span>
                      Select Target Exam
                    </h4>
                    <div className="mt-3 relative">
                      <div className="flex justify-between items-center mb-1">
                        <Label>Select Exam (Searchable)</Label>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 text-[10px] text-blue-600 hover:bg-blue-50"
                          onClick={fetchExams}
                        >
                          <Clock className="w-3 h-3 mr-1" />
                          Refresh List
                        </Button>
                      </div>
                      
                      {/* Searchable Custom Select */}
                      <div className="relative">
                        <div 
                          className="w-full px-3 py-2 border rounded-lg bg-white flex justify-between items-center cursor-pointer hover:border-blue-400 transition-colors"
                          onClick={() => setShowSearchList(!showSearchList)}
                        >
                          <span className={selectedExamId ? "text-gray-900" : "text-gray-400"}>
                            {selectedExamId 
                              ? examsList.find(e => e.id === selectedExamId)?.exam_type + " - " + examsList.find(e => e.id === selectedExamId)?.class_name
                              : "-- Search and Select an Exam --"}
                          </span>
                          <Filter className="w-4 h-4 text-gray-400" />
                        </div>

                        {showSearchList && (
                          <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-xl max-h-60 overflow-hidden flex flex-col">
                            <div className="p-2 border-b bg-gray-50">
                              <input 
                                type="text"
                                className="w-full px-2 py-1 text-sm border rounded"
                                placeholder="Type to search exam name or class..."
                                value={examSearch}
                                onChange={(e) => setExamSearch(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                              />
                            </div>
                            <div className="overflow-y-auto">
                              {examsList
                                .filter(e => 
                                  e.exam_type?.toLowerCase().includes(examSearch.toLowerCase()) || 
                                  e.class_name?.toLowerCase().includes(examSearch.toLowerCase())
                                )
                                .map(exam => (
                                  <div 
                                    key={exam.id}
                                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 flex justify-between items-center ${selectedExamId === exam.id ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}
                                    onClick={() => {
                                      setSelectedExamId(exam.id);
                                      setGlobalStats(null);
                                      setShowSearchList(false);
                                      setExamSearch("");
                                    }}
                                  >
                                    <div className="flex flex-col">
                                      <span className="font-semibold">{exam.exam_type}</span>
                                      <span className="text-[10px] opacity-70">{exam.class_name} • {new Date(exam.created_at).toLocaleDateString()}</span>
                                    </div>
                                    {exam.is_legacy && (
                                      <span className="text-[9px] bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded font-bold uppercase">Legacy</span>
                                    )}
                                  </div>
                                ))}
                              {examsList.length === 0 && (
                                <div className="p-4 text-center text-xs text-gray-400 italic">No exams found</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {examsList.length === 0 && (
                        <p className="text-[10px] text-orange-600 mt-1 font-medium italic">
                          ⚠️ No exams found. Create one in "Bulk Upload" first.
                        </p>
                      )}
                    </div>

                  </div>
                </div>

                {/* Right: Upload Section */}
                <div className="space-y-4 border p-4 rounded-lg bg-gray-50 flex flex-col justify-center">
                  <h4 className="font-medium flex items-center gap-2">
                    <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">2</span>
                    Upload Day Scholar Marks
                  </h4>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-white flex-1 flex flex-col justify-center">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => setAdditionalFile(e.target.files[0])}
                      className="hidden"
                      id="day-scholar-file"
                    />
                    <label htmlFor="day-scholar-file" className="cursor-pointer">
                      <Upload className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                      <p className="text-sm text-gray-600 font-medium">
                        {additionalFile ? additionalFile.name : "Click to upload completed sheet"}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-1">Supports .xlsx and .xls</p>
                    </label>
                  </div>

                  {additionalFile && (
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-6" 
                      onClick={handleAdditionalUpload}
                      disabled={loading || !selectedExamId}
                    >
                      {loading ? "Processing..." : "Sync Global Stats"}
                    </Button>
                  )}
                  {!selectedExamId && additionalFile && (
                    <p className="text-[10px] text-red-500 text-center font-bold">
                      Please select an exam before syncing!
                    </p>
                  )}
                </div>
              </div>


              {/* Step 3: Result Preview */}
              {globalStats && (
                <div className="mt-6 border rounded-xl overflow-hidden animate-in fade-in slide-in-from-bottom-4">
                  <div className="bg-blue-600 p-4 text-white">
                    <h4 className="font-bold">✨ Global Class Statistics Generated</h4>
                    <p className="text-xs opacity-80">Calculated from {globalStats.day_scholars_included} Day Scholars + Hostellers</p>
                  </div>
                  <div className="p-4 bg-white grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-100 flex justify-between items-center">
                      <span className="font-medium text-blue-700">Class Highest %:</span>
                      <span className="text-2xl font-black text-blue-600">{globalStats.class_highest_percentage}%</span>
                    </div>
                    <div className="space-y-2">
                      <p className="text-xs font-bold text-gray-400 uppercase">Subject Averages</p>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {Object.entries(globalStats.subject_averages).map(([sub, data]) => (
                          <div key={sub} className="flex justify-between text-sm p-2 bg-gray-50 rounded">
                            <span className="text-gray-600">{sub}</span>
                            <span className="font-bold text-gray-900">{data.average} pts</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      {/* Add Results Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Exam Results</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Select Student *</Label>
                <select
                  className="w-full px-3 py-2 border rounded-lg"
                  value={formData.student_id}
                  onChange={(e) =>
                    setFormData({ ...formData, student_id: e.target.value })
                  }
                  required
                >
                  <option value="">Select a student</option>
                  {students?.map((student) => (
                    <option key={student.id} value={student.id}>
                      {student.name} - {student.class_name || "N/A"}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Exam Type *</Label>
                <select
                  className="w-full px-3 py-2 border rounded-lg"
                  value={formData?.exam_type}
                  onChange={(e) =>
                    setFormData({ ...formData, exam_type: e.target.value })
                  }
                  required
                >
                  <option value="">Select an exam type</option>
                  {examTypesData?.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {formData?.exam_type === "custom" && (
              <div>
                <Label htmlFor="CustomExamType">Custom Exam Type *</Label>
                <Input
                  id="CustomExamType"
                  type="text"
                  placeholder="Enter custom exam type"
                  value={formData.custom_exam_type}
                  required
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      custom_exam_type: e.target.value,
                    }))
                  }
                  className="mt-1"
                />
              </div>
            )}

            <div>
              <Label>Exam Date *</Label>
              <Input
                type="date"
                max={today}
                value={formData.exam_date}
                onChange={(e) =>
                  setFormData({ ...formData, exam_date: e.target.value })
                }
                required
              />
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Add Subjects</h3>

              <div className="grid grid-cols-3 gap-3 mb-3">
                <Input
                  placeholder="Subject Name"
                  value={currentSubject.subject_name}
                  onChange={(e) =>
                    setCurrentSubject({
                      ...currentSubject,
                      subject_name: e.target.value,
                    })
                  }
                />
                <Input
                  type="number"
                  placeholder="Marks Obtained"
                  value={currentSubject.marks}
                  onChange={(e) =>
                    setCurrentSubject({
                      ...currentSubject,
                      marks: e.target.value,
                    })
                  }
                />
                <Input
                  type="number"
                  placeholder="Max Marks"
                  value={currentSubject.max_marks}
                  onChange={(e) =>
                    setCurrentSubject({
                      ...currentSubject,
                      max_marks: e.target.value,
                    })
                  }
                />
              </div>

              <Button
                type="button"
                onClick={addSubject}
                variant="outline"
                className="w-full mb-4"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Subject
              </Button>

              {formData.subjects.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Added Subjects:</p>
                  {formData.subjects.map((sub, idx) => (
                    <div
                      key={idx}
                      className="flex justify-between items-center p-2 bg-gray-50 rounded"
                    >
                      <span>
                        {sub.subject_name}: {sub.marks}/{sub.max_marks}
                      </span>
                      <Button
                        type="button"
                        onClick={() => removeSubject(idx)}
                        variant="ghost"
                        size="sm"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <div className="pt-2 border-t">
                    <div className="flex justify-between font-semibold text-sm">
                      <span>Total:</span>
                      <span className="text-green-600">
                        {formData.subjects.reduce(
                          (sum, s) => sum + parseFloat(s.marks || 0),
                          0
                        )}
                        /
                        {formData.subjects.reduce(
                          (sum, s) => sum + parseFloat(s.max_marks || 0),
                          0
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary text-white"
            >
              {loading ? "Adding..." : "Add Results"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Marksheet Detail Modal */}
      <Dialog open={showMarksheetModal} onOpenChange={setShowMarksheetModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="border-b pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg text-blue-600">
                  <Award className="w-6 h-6" />
                </div>
                <div>
                  <DialogTitle className="text-xl font-bold" style={{ color: "#2C5F7F" }}>
                    Digital Marksheet
                  </DialogTitle>
                  <p className="text-sm text-gray-500">
                    {selectedMarksheet?.exam_type}
                  </p>
                </div>
              </div>
              {selectedMarksheet && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handleDeleteMarksheet(
                      selectedMarksheet.student_id,
                      selectedMarksheet.exam_type,
                      selectedMarksheet.exam_date,
                      selectedMarksheet.student_name
                    )
                  }
                  className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 gap-1.5"
                  disabled={deletingMarksheet}
                >
                  <Trash2 className="w-4 h-4" />
                  {deletingMarksheet ? "Deleting..." : "Delete Marksheet"}
                </Button>
              )}
            </div>
          </DialogHeader>

          {selectedMarksheet && (
            <div className="space-y-6 pt-4">
              {/* Marksheet Layout */}
              <div className="border-2 border-blue-100 rounded-xl p-6 bg-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full -mr-10 -mt-10 opacity-50 z-0" />
                
                {/* Header Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 relative z-10">
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <p className="text-[10px] uppercase font-bold text-gray-400">Student Name</p>
                    <p className="text-sm font-bold text-gray-700">{selectedMarksheet.student_name}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <p className="text-[10px] uppercase font-bold text-gray-400">Student ID</p>
                    <p className="text-sm font-bold text-gray-700">{selectedMarksheet.student_readable_id}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <p className="text-[10px] uppercase font-bold text-gray-400">Class</p>
                    <p className="text-sm font-bold text-gray-700">{selectedMarksheet.class_name}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <p className="text-[10px] uppercase font-bold text-gray-400">Exam Date</p>
                    <p className="text-sm font-bold text-gray-700">{new Date(selectedMarksheet.exam_date).toLocaleDateString()}</p>
                  </div>
                </div>

                {/* Subject Table */}
                <div className="overflow-hidden rounded-lg border border-gray-200 mb-8 relative z-10">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-blue-600 text-white uppercase text-[10px] tracking-wider">
                      <tr>
                        <th className="px-4 py-3">Subject Name</th>
                        <th className="px-4 py-3 text-center">Marks Obtained</th>
                        <th className="px-4 py-3 text-center">Max Marks</th>
                        <th className="px-4 py-3 text-center">Class Average</th>
                        <th className="px-4 py-3 text-center">Percentage</th>
                        <th className="px-4 py-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {selectedMarksheet.subjects.map((sub, idx) => {
                        const rowKey = sub.id || `${selectedMarksheet.student_id || 'std'}-${sub.subject_name || 'sub'}-${idx}`;
                        const isEditing = editingSubjectId === rowKey;
                        const classAvg = (() => {
                          const stats = selectedMarksheet.global_stats?.subject_averages;
                          if (!stats) return 'N/A';
                          const subName = (sub.subject_name || sub.subject || '')?.toString().toLowerCase().trim();
                          if (!subName) return 'N/A';
                          const directKey = Object.keys(stats).find(k => k?.toString().toLowerCase().trim() === subName);
                          if (directKey && stats[directKey]?.average !== undefined && stats[directKey]?.average !== null) {
                            return stats[directKey].average;
                          }
                          const fuzzyKey = Object.keys(stats).find(k => {
                            const kNorm = k?.toString().toLowerCase().trim();
                            return kNorm && (kNorm.startsWith(subName) || subName.startsWith(kNorm));
                          });
                          if (fuzzyKey && stats[fuzzyKey]?.average !== undefined && stats[fuzzyKey]?.average !== null) {
                            return stats[fuzzyKey].average;
                          }
                          return 'N/A';
                        })();
                        
                        return (
                          <tr key={rowKey} className={`transition-colors ${isEditing ? 'bg-amber-50/70' : 'hover:bg-blue-50'}`}>
                            <td className="px-4 py-3 font-semibold text-gray-700">{sub.subject_name}</td>
                            <td className="px-4 py-3 text-center">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  min="0"
                                  className="w-20 text-center mx-auto h-8 px-1 font-bold text-blue-600 bg-white"
                                  value={editForm.marks_obtained}
                                  onChange={(e) =>
                                    setEditForm((prev) => ({
                                      ...prev,
                                      marks_obtained: e.target.value,
                                    }))
                                  }
                                  autoFocus
                                />
                              ) : (
                                <span className="font-bold text-blue-600">{sub.marks_obtained}</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  min="1"
                                  className="w-20 text-center mx-auto h-8 px-1 text-gray-700 bg-white"
                                  value={editForm.total_marks}
                                  onChange={(e) =>
                                    setEditForm((prev) => ({
                                      ...prev,
                                      total_marks: e.target.value,
                                    }))
                                  }
                                />
                              ) : (
                                <span className="text-gray-500">{sub.total_marks}</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center font-semibold text-gray-600">{classAvg}</td>
                            <td className="px-4 py-3 text-center">
                              {isEditing ? (
                                <span className="font-bold text-blue-600">
                                  {editForm.marks_obtained !== "" && parseFloat(editForm.total_marks) > 0
                                    ? ((parseFloat(editForm.marks_obtained) / parseFloat(editForm.total_marks)) * 100).toFixed(1) + "%"
                                    : "-"}
                                </span>
                              ) : (
                                <span className={`font-bold ${sub.percentage >= 40 ? 'text-green-600' : 'text-red-600'}`}>
                                  {sub.percentage}%
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                {isEditing ? (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-100"
                                      title="Save marks"
                                      disabled={savingEdit}
                                      onClick={() => handleSaveSubjectEdit(sub.id || editForm.result_id)}
                                    >
                                      <Check className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-gray-500 hover:text-gray-700 hover:bg-gray-200"
                                      title="Cancel"
                                      onClick={() => setEditingSubjectId(null)}
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-blue-600 hover:text-blue-800 hover:bg-blue-100"
                                      title="Edit marks"
                                      onClick={() => {
                                        setEditingSubjectId(rowKey);
                                        setEditForm({
                                          result_id: sub.id,
                                          marks_obtained: sub.marks_obtained,
                                          total_marks: sub.total_marks,
                                          subject_name: sub.subject_name,
                                        });
                                      }}
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </Button>
                                    <Button 
                                      variant="ghost" 
                                      size="icon" 
                                      className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                                      title="Delete this subject"
                                      onClick={() => handleDeleteResult(sub.id)}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-gray-50 font-bold border-t-2 border-blue-100">
                      <tr>
                        <td className="px-4 py-4 text-blue-600">GRAND TOTAL</td>
                        <td className="px-4 py-4 text-center text-blue-600 text-lg">
                          {selectedMarksheet.total_obtained}
                        </td>
                        <td className="px-4 py-4 text-center text-gray-500">
                          {selectedMarksheet.total_max}
                        </td>
                        <td className="px-4 py-4 text-center text-gray-400 font-normal">
                          -
                        </td>
                        <td className="px-4 py-4 text-center">
                          <div className={`text-lg ${selectedMarksheet.overall_percentage >= 40 ? 'text-green-600' : 'text-red-600'}`}>
                            {selectedMarksheet.overall_percentage}%
                          </div>
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Performance Summary */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 relative z-10">
                  <div className="flex flex-col items-center p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <p className="text-[10px] font-bold text-blue-400 uppercase mb-1">Overall Grade</p>
                    <p className="text-3xl font-black text-blue-600">{selectedMarksheet.grade}</p>
                  </div>
                  <div className="flex flex-col items-center p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <p className="text-[10px] font-bold text-blue-400 uppercase mb-1">Pass Status</p>
                    <p className={`text-2xl font-black ${selectedMarksheet.status === 'PASSED' ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedMarksheet.status}
                    </p>
                  </div>
                  <div className="flex flex-col items-center p-4 bg-yellow-50 rounded-xl border border-yellow-100">
                    <p className="text-[10px] font-bold text-yellow-500 uppercase mb-1">Class Highest</p>
                    <p className="text-3xl font-black text-yellow-600">
                      {selectedMarksheet.global_stats?.class_highest_percentage !== undefined && selectedMarksheet.global_stats?.class_highest_percentage !== null
                        ? `${selectedMarksheet.global_stats.class_highest_percentage}%`
                        : 'N/A'}
                    </p>
                  </div>
                  {selectedMarksheet.rank ? (
                    <div className="flex flex-col items-center p-4 bg-purple-50 rounded-xl border border-purple-100">
                      <p className="text-[10px] font-bold text-purple-400 uppercase mb-1">Class Rank</p>
                      <p className="text-3xl font-black text-purple-600">#{selectedMarksheet.rank}</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center p-4 bg-blue-50 rounded-xl border border-blue-100">
                      <p className="text-[10px] font-bold text-blue-400 uppercase mb-1">Remarks</p>
                      <p className="text-sm font-medium text-gray-600 text-center italic">
                        {selectedMarksheet.overall_percentage >= 75 ? "Excellent Performance!" : 
                         selectedMarksheet.overall_percentage >= 60 ? "Good Job, keep improving." : 
                         selectedMarksheet.overall_percentage >= 40 ? "Can do better." : "Needs hard work."}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Grading Scheme Reference */}
              <div className="bg-gray-50/50 border border-gray-200/65 rounded-xl p-4 shadow-none">
                <p className="text-xs font-semibold text-gray-500 flex items-center gap-1.5 uppercase tracking-wider mb-2.5">
                  <Award className="w-3.5 h-3.5 text-blue-500" />
                  Grading & Evaluation Scheme Reference
                </p>
                <div className="grid grid-cols-4 md:grid-cols-7 gap-1 text-center font-semibold text-[11px] text-gray-500">
                  <div className="p-1 bg-green-50 text-green-700 rounded border border-green-100">
                    <span className="block font-bold">A+</span> &ge;90%
                  </div>
                  <div className="p-1 bg-blue-50 text-blue-700 rounded border border-blue-100">
                    <span className="block font-bold">A</span> 80-89%
                  </div>
                  <div className="p-1 bg-indigo-50 text-indigo-700 rounded border border-indigo-100">
                    <span className="block font-bold">B</span> 70-79%
                  </div>
                  <div className="p-1 bg-yellow-50 text-yellow-700 rounded border border-yellow-100">
                    <span className="block font-bold">C</span> 60-69%
                  </div>
                  <div className="p-1 bg-orange-50 text-orange-700 rounded border border-orange-100">
                    <span className="block font-bold">D</span> 50-59%
                  </div>
                  <div className="p-1 bg-amber-50/70 text-amber-700 rounded border border-amber-100">
                    <span className="block font-bold">E</span> 40-49%
                  </div>
                  <div className="p-1 bg-red-50 text-red-700 rounded border border-red-100">
                    <span className="block font-bold">F</span> &lt;40%
                  </div>
                </div>
                <p className="text-[10px] text-center italic text-gray-400 mt-2">
                  * Minimum passing criteria is <strong>33%</strong>. Overall Grade is calculated on the cumulative percentage of all subjects.
                </p>
              </div>

              <div className="flex justify-end gap-3 no-print">
                <Button variant="outline" onClick={() => window.print()} className="gap-2">
                  <Download className="w-4 h-4" />
                  Print Marksheet
                </Button>
                <Button 
                  onClick={() => setShowMarksheetModal(false)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={showBulkDeleteModal} onOpenChange={setShowBulkDeleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              <DialogTitle className="text-lg font-bold text-red-600">
                Bulk Delete Exam Results
              </DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <div className="bg-red-50 border border-red-200 p-3 rounded-lg text-xs text-red-700">
              <p className="font-semibold mb-1">⚠️ Warning: Irreversible Action</p>
              <p>
                This will permanently delete all student results matching the selected exam, class, and date.
              </p>
            </div>

            <div>
              <Label htmlFor="bulkDeleteExam">Exam Type *</Label>
              <select
                id="bulkDeleteExam"
                className="w-full px-3 py-2 border rounded-lg mt-1 text-sm"
                value={bulkDeleteForm.exam_type}
                onChange={(e) =>
                  setBulkDeleteForm((prev) => ({ ...prev, exam_type: e.target.value }))
                }
              >
                <option value="">Select Exam Type to Delete</option>
                {examTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="bulkDeleteClass">Class (Optional)</Label>
              <select
                id="bulkDeleteClass"
                className="w-full px-3 py-2 border rounded-lg mt-1 text-sm"
                value={bulkDeleteForm.class_name}
                onChange={(e) =>
                  setBulkDeleteForm((prev) => ({ ...prev, class_name: e.target.value }))
                }
              >
                <option value="">All Classes</option>
                {classes.map((cls) => (
                  <option key={cls} value={cls}>
                    {cls}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="bulkDeleteDate">Exam Date (Optional)</Label>
              <Input
                id="bulkDeleteDate"
                type="date"
                className="mt-1 text-sm"
                value={bulkDeleteForm.exam_date}
                onChange={(e) =>
                  setBulkDeleteForm((prev) => ({ ...prev, exam_date: e.target.value }))
                }
              />
              <p className="text-[11px] text-gray-500 mt-0.5">
                Leave empty to delete results across all dates for this exam.
              </p>
            </div>

            <div className="pt-2 border-t">
              <Label htmlFor="confirmDelete">
                Type <span className="font-mono font-bold text-red-600">DELETE</span> to confirm *
              </Label>
              <Input
                id="confirmDelete"
                type="text"
                placeholder="DELETE"
                className="mt-1 font-mono text-sm"
                value={bulkDeleteForm.confirm_text}
                onChange={(e) =>
                  setBulkDeleteForm((prev) => ({ ...prev, confirm_text: e.target.value }))
                }
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowBulkDeleteModal(false);
                  setBulkDeleteForm({ exam_type: "", exam_date: "", class_name: "", confirm_text: "" });
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={
                  !bulkDeleteForm.exam_type ||
                  bulkDeleteForm.confirm_text !== "DELETE" ||
                  bulkDeleting
                }
                onClick={handleBulkDeleteExam}
              >
                {bulkDeleting ? "Deleting..." : "Permanently Delete"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResultsManagement;
